import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, AlertCircle, RefreshCw, Search, Download, Mail, QrCode, Check,
  GraduationCap, Users, BadgeCheck, CreditCard, Settings2, X, Send, Trash2,
  FileText, ExternalLink, FileCheck2, Clock, ThumbsUp, Undo2,
} from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";
import PledgeQrPanel from "../components/PledgeQrPanel";
import {
  useGetPledgeSubmissionsQuery,
  useUpdatePledgeSubmissionMutation,
  useDeletePledgeSubmissionMutation,
  useResendPledgeFormMutation,
  useRecordHodDecisionMutation,
  useEmailPledgeStudentsMutation,
  pledgeParams,
  type PledgeFilters,
  type PledgeSubmission,
  type TriState,
  type HodState,
} from "../api/graduatePledgeApi";

/**
 * The Secretariat's register of pledge submissions.
 *
 * One screen, because the brief describes one job: watch what is coming in per
 * institution before the cut-off, then after the ceremony work down the list
 * ticking off who attended, whose graduation was confirmed and who has been
 * issued a membership number.
 *
 * The export and the mail-out deliberately take the SAME filters the table is
 * showing. A spreadsheet that quietly covered a different set from the screen
 * it was taken from — or a reminder that reached a list nobody had looked at —
 * is the failure worth designing against here, so there is one filter state
 * and all three read it.
 */

const TRI_OPTIONS = [
  { value: "", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "pending", label: "Not checked" },
];

function fmtDateTime(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T")).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function Tile({
  icon, label, value, tone = "slate",
}: { icon: React.ReactNode; label: string; value: number | string; tone?: string }) {
  const tones: Record<string, string> = {
    slate: "bg-slate-50 text-slate-600 border-slate-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${tones[tone] ?? tones.slate}`}>
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest opacity-70">
        {icon}{label}
      </div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

/** Yes / No / not-yet, as a cell you can read at a glance down a column. */
function TriBadge({ value }: { value: TriState }) {
  if (value === null || value === undefined) {
    return <span className="text-xs text-slate-300">—</span>;
  }
  return value
    ? <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Yes</span>
    : <span className="text-xs font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">No</span>;
}

/** The Head of Department's answer at a glance, down a column. */
function HodBadge({ hod }: { hod?: HodState }) {
  const map: Record<string, string> = {
    approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
    pending:  "text-sky-700 bg-sky-50 border-sky-200",
    declined: "text-amber-700 bg-amber-50 border-amber-200",
  };
  const label: Record<string, string> = {
    approved: "Confirmed", pending: "Awaiting HOD", declined: "Declined",
  };
  const status = hod?.status ?? "pending";

  return (
    <div>
      <span className={`text-xs font-bold rounded px-1.5 py-0.5 border ${map[status]}`}>
        {label[status]}
      </span>
      {/* Waiting on a department with nobody in the portal is not the same
          as waiting on a department that simply has not got to it. */}
      {status === "pending" && hod && !hod.linked && (
        <div className="text-[10px] text-amber-600 mt-0.5">no HOD linked</div>
      )}
      {status !== "pending" && hod?.by_secretariat && (
        <div className="text-[10px] text-slate-400 mt-0.5">by the Secretariat</div>
      )}
    </div>
  );
}

const selectCls =
  "rounded-lg border border-slate-300 px-2.5 py-2 text-sm text-slate-700 bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-200";

export default function AdminGraduatePledgePage() {
  const [filters, setFilters] = useState<PledgeFilters>({ page: 1, per_page: 25 });
  const [search, setSearch] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [exporting, setExporting] = useState<"csv" | "xlsx" | null>(null);
  const [exportError, setExportError] = useState("");
  const [editing, setEditing] = useState<PledgeSubmission | null>(null);
  const [mailOpen, setMailOpen] = useState(false);

  const { data, isLoading, isFetching, error, refetch } = useGetPledgeSubmissionsQuery(filters);

  const rows = data?.data ?? [];
  const meta = data?.meta ?? ({} as any);
  const pagination = data?.pagination;
  const totals = meta?.totals ?? {
    submissions: 0, attended: 0, graduation_confirmed: 0, membership_issued: 0, not_yet_member: 0,
  };

  // Keep an open drawer in step with the list behind it. Reviewing a signed
  // form invalidates the list, and without this the drawer would go on showing
  // the row as it was when it was opened — so an accepted form would still
  // offer an Accept button. The drawer's own in-progress edits are local
  // state, so they survive this.
  useEffect(() => {
    if (!editing) return;
    const fresh = (data?.data ?? []).find((r) => r.id === editing.id);
    if (fresh && fresh !== editing) setEditing(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const setFilter = (patch: Partial<PledgeFilters>) =>
    // Any change to a filter resets to page 1: page 4 of a narrower list is
    // usually empty, and an empty screen reads as "nothing matched".
    setFilters((f) => ({ ...f, ...patch, page: 1 }));

  const publicUrl = meta?.cohort?.public_url
    ?? `${window.location.origin}/graduate-pledge`;

  /**
   * RTK Query parses JSON, so the sheet is fetched by hand with the token
   * attached — the same way the event report export does it.
   */
  const download = async (format: "csv" | "xlsx") => {
    setExporting(format);
    setExportError("");
    try {
      const token = localStorage.getItem("token");
      const qs = new URLSearchParams({
        ...pledgeParams({ ...filters, page: undefined, per_page: undefined }),
        format,
      } as any).toString();

      const res = await fetch(`${BASE_URL}/admin/graduate-pledge/submissions/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (HTTP ${res.status}).`);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `niqs-graduate-pledge-${meta?.cohort?.year ?? ""}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setExportError(e?.message || "Could not download the export.");
    } finally {
      setExporting(null);
    }
  };

  const institutions = meta?.options?.institutions ?? [];
  const programmes: Record<string, string> = meta?.options?.programmes ?? {};

  const filtersActive = useMemo(
    () => Boolean(
      filters.q || filters.institution_id || filters.programme || filters.interest_area ||
      filters.attended || filters.graduation_confirmed || filters.membership_issued ||
      filters.hod,
    ),
    [filters],
  );

  return (
    <div className="p-4 sm:p-6 space-y-5 bg-white rounded-lg shadow-sm">

      {/* ── Head ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Graduate Pledge</h2>
          <p className="text-xs text-gray-500 font-medium mt-1 max-w-2xl">
            Final-year students&rsquo; Declarations of Intent, submitted online before the
            Pledge Ceremony. Each student prints their own form, signs it and has it stamped
            by their Head of Department.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/graduate-pledge/cohorts"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm
                       text-gray-600 hover:bg-gray-50"
          >
            <Settings2 className="w-4 h-4" /> Cohorts
          </Link>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2.5 rounded-lg border text-gray-500 hover:bg-gray-50 disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {!!error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{(error as any)?.data?.message || "Could not load the submissions."}</span>
        </div>
      )}

      {!isLoading && !meta?.cohort && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center">
          <p className="text-sm text-amber-900 font-semibold">No pledge cohort has been set up yet.</p>
          <p className="text-xs text-amber-800 mt-1">
            Create one under <Link to="/admin/graduate-pledge/cohorts" className="underline">Cohorts</Link>,
            choose the pilot institutions and set the cut-off date, then share the form link.
          </p>
        </div>
      )}

      {meta?.cohort && (
        <>
          {/* ── Cohort bar + the link that goes on noticeboards ──────── */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-3 flex-1">
              <select
                className={selectCls}
                value={String(filters.cohort_id ?? meta.cohort.id)}
                onChange={(e) => setFilter({ cohort_id: e.target.value })}
              >
                {(meta.cohorts ?? []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.year}){c.is_open ? " — open" : ""}
                  </option>
                ))}
              </select>
              <span
                className={`text-[11px] font-bold px-2 py-1 rounded border ${
                  meta.cohort.accepting
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : "text-slate-500 bg-slate-100 border-slate-200"
                }`}
              >
                {meta.cohort.accepting ? "Accepting submissions" : "Closed"}
              </span>
              <span className="text-xs text-slate-500 hidden sm:inline">
                Cut-off {meta.cohort.closes_on}, 23:59
              </span>
            </div>

            <button
              onClick={() => setShowShare((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border bg-white text-sm
                         text-gray-600 hover:bg-gray-50 self-start"
            >
              <QrCode className="w-4 h-4" />
              {showShare ? "Hide the form link" : "Share the form"}
            </button>
          </div>

          {/* Folded away by default: this is the screen staff live on, and the
              QR is needed when a department asks for it, not every morning. */}
          {showShare && (
            <PledgeQrPanel
              url={publicUrl}
              cohortName={meta.cohort.name}
              closesOn={meta.cohort.closes_on}
              ceremonyDate={meta.cohort.ceremony_date ?? undefined}
            />
          )}

          {/* ── Tiles ────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Tile icon={<Users className="w-3 h-3" />} label="Submissions" value={totals.submissions} tone="indigo" />
            {/* The pre-ceremony queue. Two numbers, because "waiting on us"
                and "waiting on the student" are different work. */}
            <Tile
              icon={<FileCheck2 className="w-3 h-3" />}
              label="Awaiting HOD"
              value={`${totals.hod_pending ?? 0}${(totals.hod_stuck ?? 0) > 0 ? ` · ${totals.hod_stuck} stuck` : ""}`}
              tone={(totals.hod_stuck ?? 0) > 0 ? "amber" : "slate"}
            />
            <Tile icon={<GraduationCap className="w-3 h-3" />} label="Attended" value={totals.attended} tone="emerald" />
            <Tile icon={<BadgeCheck className="w-3 h-3" />} label="Graduation confirmed" value={totals.graduation_confirmed} />
            <Tile icon={<CreditCard className="w-3 h-3" />} label="Membership no. issued" value={totals.membership_issued} tone="amber" />
          </div>

          {/* ── Per institution ──────────────────────────────────────── */}
          {(meta.by_institution ?? []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {meta.by_institution.map((i: any) => {
                const active = String(filters.institution_id ?? "") === String(i.id);
                return (
                  <button
                    key={i.id}
                    onClick={() => setFilter({ institution_id: active ? "" : i.id })}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition ${
                      active
                        ? "border-indigo-400 bg-indigo-50 text-indigo-700 font-bold"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                    title={i.retired
                      ? `${i.name} — no longer on this cohort's list, but its submissions still count`
                      : i.name}
                  >
                    {i.acronym || i.name}
                    {i.retired && <span className="ml-1 text-slate-400">·off list</span>}
                    <span className="ml-1.5 font-black">{i.total}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Filters ──────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="Name, form no., matric no., email or phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setFilter({ q: search.trim() }); }}
                onBlur={() => setFilter({ q: search.trim() })}
              />
            </div>

            <select
              className={selectCls}
              value={String(filters.programme ?? "")}
              onChange={(e) => setFilter({ programme: e.target.value })}
            >
              <option value="">All programmes</option>
              {Object.entries(programmes).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>

            <select
              className={selectCls}
              value={String(filters.attended ?? "")}
              onChange={(e) => setFilter({ attended: e.target.value as any })}
            >
              {TRI_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>Attended: {o.label}</option>
              ))}
            </select>

            <select
              className={selectCls}
              value={String(filters.graduation_confirmed ?? "")}
              onChange={(e) => setFilter({ graduation_confirmed: e.target.value as any })}
            >
              {TRI_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>Graduation: {o.label}</option>
              ))}
            </select>

            <select
              className={selectCls}
              value={String(filters.hod ?? "")}
              onChange={(e) => setFilter({ hod: e.target.value as any })}
            >
              <option value="">HOD: All</option>
              <option value="pending">HOD: Awaiting</option>
              <option value="stuck">HOD: Stuck — nobody linked</option>
              <option value="approved">HOD: Confirmed</option>
              <option value="declined">HOD: Declined</option>
            </select>

            <select
              className={selectCls}
              value={String(filters.membership_issued ?? "")}
              onChange={(e) => setFilter({ membership_issued: e.target.value as any })}
            >
              <option value="">Mem. no.: All</option>
              <option value="yes">Mem. no.: Issued</option>
              <option value="no">Mem. no.: Not issued</option>
            </select>

            {filtersActive && (
              <button
                onClick={() => { setSearch(""); setFilters({ cohort_id: filters.cohort_id, page: 1, per_page: filters.per_page }); }}
                className="text-xs text-slate-500 underline px-2"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* ── Actions on the filtered set ──────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <span className="text-xs text-slate-500">
              {meta.filtered_total ?? 0} student{(meta.filtered_total ?? 0) === 1 ? "" : "s"}
              {filtersActive ? " match these filters" : " in this cohort"}
            </span>
            <div className="flex-1" />
            <button
              onClick={() => download("xlsx")}
              disabled={!!exporting}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              {exporting === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Excel
            </button>
            <button
              onClick={() => download("csv")}
              disabled={!!exporting}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              {exporting === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              CSV
            </button>
            <button
              onClick={() => setMailOpen(true)}
              disabled={(meta.filtered_total ?? 0) === 0}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40"
            >
              <Mail className="w-4 h-4" /> Email this list
            </button>
          </div>

          {exportError && (
            <div className="text-xs text-red-600">{exportError}</div>
          )}

          {/* ── Table ────────────────────────────────────────────────── */}
          <div className="overflow-x-auto border rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5 font-black">Form no.</th>
                  <th className="text-left px-3 py-2.5 font-black">Student</th>
                  <th className="text-left px-3 py-2.5 font-black">Institution</th>
                  <th className="text-left px-3 py-2.5 font-black">Matric no.</th>
                  <th className="text-left px-3 py-2.5 font-black">Prog.</th>
                  <th className="text-left px-3 py-2.5 font-black">Graduation</th>
                  <th className="text-left px-3 py-2.5 font-black">HOD</th>
                  <th className="text-center px-3 py-2.5 font-black">Att.</th>
                  <th className="text-center px-3 py-2.5 font-black">Grad. conf.</th>
                  <th className="text-left px-3 py-2.5 font-black">Mem. no.</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading && (
                  <tr><td colSpan={11} className="px-3 py-10 text-center">
                    <Loader2 className="w-5 h-5 text-indigo-400 animate-spin mx-auto" />
                  </td></tr>
                )}

                {!isLoading && rows.length === 0 && (
                  <tr><td colSpan={11} className="px-3 py-10 text-center text-sm text-slate-400">
                    {filtersActive ? "No student matches these filters." : "No submissions yet."}
                  </td></tr>
                )}

                {rows.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-mono text-xs font-bold text-[#0b2e6b] whitespace-nowrap">
                      {s.form_no}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-slate-800">{s.name}</div>
                      <div className="text-xs text-slate-400">{s.email}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[180px] truncate" title={s.institution}>
                      {s.institution}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{s.matric_no}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{s.programme_label}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{s.graduation}</td>
                    <td className="px-3 py-2.5"><HodBadge hod={s.hod} /></td>
                    <td className="px-3 py-2.5 text-center"><TriBadge value={s.attended} /></td>
                    <td className="px-3 py-2.5 text-center"><TriBadge value={s.graduation_confirmed} /></td>
                    <td className="px-3 py-2.5 text-xs font-semibold text-slate-700">
                      {s.membership_no_issued || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <AuthedFileButton
                        url={s.pdf_url}
                        fileName={`${s.form_no.replace(/\//g, "-")}.pdf`}
                        title="Open the student's form"
                        icon={<FileText className="w-4 h-4" />}
                        className="inline-flex p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                      />
                      <button
                        onClick={() => setEditing(s)}
                        className="inline-flex p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                        title="Open record"
                      >
                        <Settings2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.last_page > 1 && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Page {pagination.current_page} of {pagination.last_page} · {pagination.total} total</span>
              <div className="flex gap-2">
                <button
                  disabled={pagination.current_page <= 1}
                  onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
                  className="px-3 py-1.5 rounded border disabled:opacity-40 hover:bg-slate-50"
                >
                  Previous
                </button>
                <button
                  disabled={pagination.current_page >= pagination.last_page}
                  onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
                  className="px-3 py-1.5 rounded border disabled:opacity-40 hover:bg-slate-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {editing && (
        <RecordDrawer submission={editing} onClose={() => setEditing(null)} />
      )}

      {mailOpen && (
        <MailDialog
          filters={filters}
          count={meta?.filtered_total ?? 0}
          onClose={() => setMailOpen(false)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/* One student's record                                                    */
/* ══════════════════════════════════════════════════════════════════════ */

/**
 * The three post-event columns, with everything the student submitted beside
 * them so the clerk ticking a box can see who they are ticking.
 *
 * Only what has changed is posted — the server writes only the keys it is
 * sent, so saving "attended" cannot quietly write back a stale copy of the
 * student's email.
 */
function RecordDrawer({
  submission, onClose,
}: { submission: PledgeSubmission; onClose: () => void }) {
  const [attended, setAttended] = useState<TriState>(submission.attended ?? null);
  const [confirmed, setConfirmed] = useState<TriState>(submission.graduation_confirmed ?? null);
  const [memNo, setMemNo] = useState(submission.membership_no_issued ?? "");
  const [notes, setNotes] = useState(submission.admin_notes ?? "");
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [update, { isLoading: saving }] = useUpdatePledgeSubmissionMutation();
  const [remove, { isLoading: deleting }] = useDeletePledgeSubmissionMutation();
  const [resend, { isLoading: resending }] = useResendPledgeFormMutation();
  const [resent, setResent] = useState(false);

  const save = async () => {
    setSaveError("");
    try {
      await update({
        id: submission.id,
        body: {
          attended,
          graduation_confirmed: confirmed,
          membership_no_issued: memNo.trim() || null,
          admin_notes: notes.trim() || null,
        },
      }).unwrap();
      setSaved(true);
      setTimeout(onClose, 600);
    } catch (e: any) {
      setSaveError(e?.data?.message || "Could not save.");
    }
  };

  const doDelete = async () => {
    setSaveError("");
    try {
      await remove(submission.id).unwrap();
      onClose();
    } catch (e: any) {
      setSaveError(e?.data?.message || "Could not delete the record.");
    }
  };

  const Tri = ({ value, onChange }: { value: TriState; onChange: (v: TriState) => void }) => (
    <div className="flex gap-2">
      {([["Yes", true], ["No", false], ["Not checked", null]] as [string, TriState][]).map(
        ([label, v]) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(v)}
            className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${
              value === v
                ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                : "border-slate-200 text-slate-500 hover:bg-slate-50"
            }`}
          >
            {label}
          </button>
        ),
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-mono text-xs font-bold text-[#0b2e6b]">{submission.form_no}</div>
            <div className="font-bold text-slate-800">{submission.name}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* What the student submitted — read-only here on purpose. */}
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[
              ["Institution", submission.institution],
              ["Matriculation no.", submission.matric_no],
              ["Programme", submission.programme_label],
              ["Expected graduation", submission.graduation],
              ["Student membership no.", submission.student_membership_no || (submission.not_yet_member ? "Not yet a member" : "—")],
              ["Email", submission.email],
              ["Phone (WhatsApp)", submission.phone],
              ["Alternative phone", submission.alt_phone || "—"],
              ["State after graduation", submission.state_after_graduation],
              ["Area of interest", submission.interest_label],
              ["Submitted", fmtDateTime(submission.submitted_at)],
            ].map(([k, v]) => (
              <div key={k as string} className="flex justify-between gap-3 border-b border-slate-50 py-1">
                <dt className="text-slate-400 text-xs">{k}</dt>
                <dd className="font-semibold text-slate-700 text-right text-xs">{v}</dd>
              </div>
            ))}
          </dl>

          {/* Before the ceremony: has their Head of Department confirmed
              them? Above the post-event block because it is the question with
              a deadline. */}
          <HodDecisionPanel submission={submission} />

          <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-700">
              Secretariat — after the event
            </div>

            <div>
              <div className="text-xs font-bold text-slate-600 mb-1.5">Attended the ceremony</div>
              <Tri value={attended} onChange={setAttended} />
            </div>

            <div>
              <div className="text-xs font-bold text-slate-600 mb-1.5">Graduation confirmed</div>
              <Tri value={confirmed} onChange={setConfirmed} />
            </div>

            <div>
              <div className="text-xs font-bold text-slate-600 mb-1.5">Membership no. issued</div>
              <input
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white
                           focus:outline-none focus:ring-2 focus:ring-indigo-200"
                value={memNo}
                onChange={(e) => setMemNo(e.target.value)}
                placeholder="Leave blank until a number is issued"
              />
            </div>

            <div>
              <div className="text-xs font-bold text-slate-600 mb-1.5">Notes</div>
              <textarea
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white
                           focus:outline-none focus:ring-2 focus:ring-indigo-200"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {saveError && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{saveError}</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <AuthedFileButton
              url={submission.pdf_url}
              fileName={`${submission.form_no.replace(/\//g, "-")}.pdf`}
              icon={<ExternalLink className="w-4 h-4" />}
              label="Open form"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50"
            />
            <button
              onClick={async () => {
                try { await resend(submission.id).unwrap(); setResent(true); } catch { /* shown below */ }
              }}
              disabled={resending}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {resent ? "Sent" : "Re-send to student"}
            </button>

            <div className="flex-1" />

            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-600 font-semibold">Delete permanently?</span>
                <button
                  onClick={doDelete}
                  disabled={deleting}
                  className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-40"
                >
                  {deleting ? "Deleting…" : "Yes, delete"}
                </button>
                <button onClick={() => setConfirmDelete(false)} className="text-xs text-slate-500 underline">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-sm text-red-600 hover:bg-red-50"
                title="For test entries, and for a student who asks to be erased"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}

            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saved ? "Saved" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/* Recording the HOD's answer on their behalf                              */
/* ══════════════════════════════════════════════════════════════════════ */

/**
 * The Head of Department confirms their own students in the portal. This is
 * the Secretariat's fallback for the institutions with nobody matched to a
 * member — about two-thirds of the register — whose students would otherwise
 * wait on somebody who cannot sign in.
 *
 * It says so in as many words, and the decision is stamped as the
 * Secretariat's, because "the department confirmed this" is evidence and
 * "we confirmed it for them" is an administrative act.
 */
function HodDecisionPanel({ submission }: { submission: PledgeSubmission }) {
  const hod = submission.hod;
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const [record, { isLoading }] = useRecordHodDecisionMutation();

  const act = async (status: "approved" | "declined") => {
    setError("");
    if (status === "declined" && !reason.trim()) {
      setError("Say why — the student is shown this word for word.");
      return;
    }
    try {
      await record({ id: submission.id, status, notes: reason.trim() || undefined }).unwrap();
      setDeclining(false);
      setReason("");
    } catch (e: any) {
      setError(e?.data?.message || "Could not save that.");
    }
  };

  const decided = hod?.status !== "pending";

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          Head of Department
        </div>
        <HodBadge hod={hod} />
      </div>

      <div className="text-xs text-slate-600">
        {hod?.hod_name
          ? <>On the register: <strong>{hod.hod_name}</strong>{hod.linked ? " — has a portal account" : " — not matched to a member"}</>
          : "No Head of Department recorded for this institution."}
      </div>

      {hod?.status === "declined" && hod.notes && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Declined: {hod.notes}
        </div>
      )}

      {error && <div className="text-xs text-red-600">{error}</div>}

      {decided ? (
        <p className="text-[11px] text-slate-400">
          Answered {hod?.decided_at}
          {hod?.by_secretariat ? " by the Secretariat" : " by the department"}.
        </p>
      ) : hod?.linked ? (
        <p className="text-[11px] text-slate-400">
          Waiting on the department, who can confirm this themselves. Only step in if they
          cannot.
        </p>
      ) : null}

      {!decided && (
        declining ? (
          <div className="space-y-2">
            <textarea
              rows={2}
              autoFocus
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
              placeholder="e.g. Not registered in this department this session."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                onClick={() => act("declined")}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold disabled:opacity-40"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Decline &amp; email the student
              </button>
              <button onClick={() => setDeclining(false)} className="text-xs text-slate-500 underline">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => act("approved")}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
              Confirm for them
            </button>
            <button
              onClick={() => setDeclining(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-50"
            >
              <Undo2 className="w-4 h-4" /> Decline
            </button>
          </div>
        )
      )}
    </div>
  );
}

/**
 * Opens a file that lives behind an authenticated API route.
 *
 * Every file on this screen — the rendered declaration, the signed scan the
 * student sent back — is served by an endpoint sitting behind
 * `permission:graduate.pledge.access`. A plain `<a href>` sends no
 * Authorization header, so the browser arrives as a stranger and the new tab
 * shows `{"message":"Unauthenticated."}` as raw JSON. Fetching with the token
 * and handing the browser a blob: URL is the only way to open one of these.
 *
 * The blank tab is opened SYNCHRONOUSLY, before the await. A window.open()
 * that runs after a network round trip has left the user-gesture stack and
 * gets swallowed by the popup blocker; opening it first and pointing it at the
 * blob when it arrives is what makes this work in Safari. If the blocker takes
 * it anyway, the file is saved instead of shown — which is a worse outcome
 * than a tab, and a much better one than nothing happening.
 */
function AuthedFileButton({
  url, className, title, icon, label, fileName,
}: {
  url: string;
  className?: string;
  title?: string;
  icon?: React.ReactNode;
  label?: string;
  /** Name used if the popup blocker forces a download instead of a tab. */
  fileName?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const open = async () => {
    setBusy(true);
    setError("");

    const tab = window.open("", "_blank", "noopener");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

      if (!res.ok) {
        // The API answers an expired session with 500 + "Unauthenticated."
        // rather than a 401, so the body is what identifies it, not the status.
        const body = await res.text().catch(() => "");
        throw new Error(
          /Unauthenticated/i.test(body)
            ? "Your session has expired — sign in again."
            : `Could not open the file (HTTP ${res.status}).`,
        );
      }

      const blobUrl = window.URL.createObjectURL(await res.blob());

      if (tab) {
        tab.location.href = blobUrl;
      } else {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName || "document";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      // Revoked late: revoking straight away can race the new tab's own load.
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e: any) {
      tab?.close();
      setError(e?.message || "Could not open the file.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={open}
        disabled={busy}
        title={error || title}
        className={`${className ?? ""} ${error ? "text-red-600" : ""} disabled:opacity-40`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
        {label}
      </button>
      {/* Only where there is room for it. The icon-only variant in the table
          turns red and carries the reason in its tooltip instead. */}
      {error && label && <span className="text-[11px] text-red-600">{error}</span>}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/* Mail-out                                                                */
/* ══════════════════════════════════════════════════════════════════════ */

/**
 * The reminder before the cut-off, and the letter after results.
 *
 * Says who it is going to, in plain numbers, before it sends — a mail-out
 * whose audience is "whatever the filters happen to be" has to show the
 * audience, not describe it.
 */
function MailDialog({
  filters, count, onClose,
}: { filters: PledgeFilters; count: number; onClose: () => void }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [greet, setGreet] = useState(true);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  const [send, { isLoading }] = useEmailPledgeStudentsMutation();

  const submit = async () => {
    setError("");
    if (!subject.trim() || !body.trim()) {
      setError("A subject and a message are both required.");
      return;
    }
    try {
      const res = await send({
        filters: { ...filters, page: undefined, per_page: undefined },
        subject: subject.trim(),
        body: body.trim(),
        greet_by_name: greet,
      }).unwrap();
      setResult(res?.message || "Queued.");
    } catch (e: any) {
      setError(e?.data?.message || "Could not send the message.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl max-h-[92vh] overflow-y-auto">
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <div className="font-bold text-slate-800">Email students</div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2 text-xs text-indigo-800">
            Going to <strong>{count}</strong> student{count === 1 ? "" : "s"} — exactly the list
            currently on screen. Each copy is sent individually, so no student sees another&rsquo;s
            address.
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
              Subject
            </label>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Reminder: Graduate Pledge form closes Friday"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
              Message
            </label>
            <textarea
              rows={8}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Plain text. Line breaks are kept."
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              className="accent-indigo-600 w-4 h-4"
              checked={greet}
              onChange={(e) => setGreet(e.target.checked)}
            />
            Open each message with the student&rsquo;s first name
          </label>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          {result && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-800">
              <Check className="w-4 h-4 mt-0.5 shrink-0" /><span>{result}</span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-slate-600 hover:bg-slate-50">
              {result ? "Close" : "Cancel"}
            </button>
            {!result && (
              <button
                onClick={submit}
                disabled={isLoading || count === 0}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send to {count}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
